import { io, type Socket } from 'socket.io-client'

/**
 * Vela Widget — embeddable customer-chat bubble.
 *
 * Loaded once per host page via:
 *   <script src="https://velaapi.altream.com/widget.js"
 *           data-vela-key="wk_..."
 *           data-position="bottom-right"
 *           data-color="#2B8FD4"></script>
 *
 * The script:
 *   1. Reads its own data-* attributes from `document.currentScript`.
 *   2. Calls GET /api/v1/widget/config to fetch persona/branding/channels.
 *   3. Builds a Shadow DOM in a host <div> appended to <body>.
 *   4. Renders a state-machine UI: closed → open (channel picker) → chat |
 *      contact_form | whatsapp.
 *
 * No React, no external deps. Single IIFE. All styles scoped inside Shadow
 * DOM so the host page can't see or override them.
 */

/* ---------------------------------------------------------------------- */
/* Types                                                                   */
/* ---------------------------------------------------------------------- */

interface WidgetConfig {
  tenantId: string
  tenantName: string
  personaId: string
  personaName: string
  greeting: string
  widgetColor: string
  widgetPosition: 'bottom-right' | 'bottom-left'
  channels: string[]
  proactiveEnabled: boolean
  proactiveDelay: number
  proactiveMessage: string
  whatsappNumber?: string
  avatarUrl?: string | null
  showVelaBranding?: boolean
  enableCsat?: boolean
  // Widget Designer — all optional; CSS fallbacks carry the Modern defaults.
  widgetWidth?: number | null
  widgetHeight?: number | null
  launcherSize?: number | null
  launcherIcon?: string | null
  launcherRadius?: number | null
  launcherShadow?: string | null
  headerHeight?: number | null
  headerFontSize?: number | null
  headerSubtext?: string | null
  messageFontSize?: number | null
  messageBubbleRadius?: number | null
  botBubbleColor?: string | null
  botTextColor?: string | null
  userBubbleColor?: string | null
  userTextColor?: string | null
  showTimestamps?: boolean
  inputFontSize?: number | null
  inputPlaceholder?: string | null
  inputRadius?: number | null
  sendButtonStyle?: string | null
  widgetFontFamily?: string | null
  consent?: {
    enabled: boolean
    text: string
    buttonText: string
    privacyPolicyUrl: string | null
  }
  cookieConsent?: {
    enabled: boolean
    policyUrl: string | null
    policyVersion: string | null
  }
  businessHours?: {
    online: boolean
    agentsOnline: boolean
    withinHours: boolean
    reason: 'outside_hours' | 'weekend' | 'holiday' | null
    holidayName: string | null
    nextOpen: string | null
    timezone: string
    summary: string
    offlineMessage: string | null
  }
  theme: WidgetThemeConfig
}

interface WidgetThemeConfig {
  template: 'modern' | 'corporate' | 'playful' | 'minimal'
  colors: {
    widget: string
    botBubble: string
    userBubble: string
    botText: string
    userText: string
    launcher: string
  }
  dimensions: {
    widgetWidth: number
    widgetHeight: number
    launcherSize: number
    launcherRadius: number
    messageBubbleRadius: number
    inputRadius: number
  }
  typography: {
    fontFamily: string
    headerFontSize: number
    messageFontSize: number
    inputFontSize: number
    headerHeight: number
  }
  content: {
    headerSubtext: string | null
    inputPlaceholder: string
    launcherIcon: 'chat' | 'message' | 'help'
    showTimestamps: boolean
    showVelaBranding: boolean
    sendButtonStyle: 'icon' | 'text' | 'both'
  }
  behavior: {
    position: 'bottom-right' | 'bottom-left'
    channels: string[]
  }
  branding: {
    botAvatar: string | null
    botName: string
  }
}

type PublicChannelId = 'ai_chat' | 'whatsapp' | 'contact_form' | 'email' | 'phone'

interface WidgetChannel {
  id: PublicChannelId
  label: string
  description: string
  icon: 'bot' | 'whatsapp' | 'mail' | 'mail-open' | 'phone'
  enabled: boolean
  waNumber?: string
  prefilledMessage?: string
  address?: string
  number?: string
}

interface ChannelBootstrap {
  channels: WidgetChannel[]
  autoOpen: PublicChannelId | null
}

interface CookiePrefs {
  necessary: true
  analytics: boolean
  functional: boolean
  marketing: boolean
  version: string | null
  savedAt: string
}

interface SessionResp {
  sessionId: string
  greeting: string
  persona: { id: string; name: string; widgetColor: string }
}

interface MessageResp {
  response: string[]
  typingDelayMs: number
  fillerPhrase: string | null
  escalate: boolean
  escalationMessage?: string
  sessionEnded: boolean
  intent: string
  sentiment: string
  confidence: number
}

type View = 'closed' | 'picker' | 'chat' | 'contact_form' | 'thanks'

interface ChatMsg {
  who: 'bot' | 'user'
  bubbles: string[]
  ts: Date
}

/* ---------------------------------------------------------------------- */
/* Read embed config off the script tag                                    */
/* ---------------------------------------------------------------------- */

const script = document.currentScript as HTMLScriptElement | null
const apiKey = script?.dataset.velaKey ?? ''
const dataPosition = (script?.dataset.position as WidgetConfig['widgetPosition'] | undefined) ?? null
const dataColor = script?.dataset.color ?? null
// Optional size overrides on the script tag. Priority: data-attr > persona > default.
const dataWidth = numAttr(script?.dataset.width)
const dataHeight = numAttr(script?.dataset.height)

function numAttr(v: string | undefined): number | null {
  if (v == null) return null
  const n = parseInt(v, 10)
  return Number.isFinite(n) ? n : null
}

// Resolve the API base from the script's `src` so the widget always talks to
// the host that served it (no hard-coded URL — works on dev + prod identically).
const apiBase = (() => {
  if (!script?.src) return ''
  try { return new URL(script.src).origin }
  catch { return '' }
})()

if (!apiKey) {
  // eslint-disable-next-line no-console
  console.warn('[vela-widget] missing data-vela-key on script tag — widget disabled')
} else {
  void boot()
}

/* ---------------------------------------------------------------------- */
/* Boot                                                                    */
/* ---------------------------------------------------------------------- */

async function boot(): Promise<void> {
  const loadingHost = document.createElement('div')
  loadingHost.id = 'vela-widget-root'
  loadingHost.style.position = 'fixed'
  loadingHost.style.zIndex = '2147483600'
  loadingHost.tabIndex = -1
  document.body.appendChild(loadingHost)
  const loadingRoot = loadingHost.attachShadow({ mode: 'open' })

  let cfg: WidgetConfig
  let bootstrap: ChannelBootstrap
  try {
    const [cfgLoaded, channelsLoaded] = await Promise.all([fetchConfig(), fetchChannels()])
    cfg = cfgLoaded
    bootstrap = channelsLoaded
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[vela-widget] config load failed; falling back to defaults', err)
    // Render a minimal-config bubble anyway so the operator sees *something*
    // — silently bailing made it impossible to debug "Test widget" failures
    // from the dashboard. The bubble can't open chat without a session, but
    // its presence proves the script reached the page.
    cfg = {
      tenantId: '',
      tenantName: 'Support',
      personaId: '',
      personaName: 'Assistant',
      greeting: 'Hi! How can we help?',
      widgetColor: dataColor || '#2B8FD4',
      widgetPosition: (dataPosition as 'bottom-right' | 'bottom-left') || 'bottom-right',
      channels: ['chat'],
      proactiveEnabled: false,
      proactiveDelay: 30,
      proactiveMessage: '',
      theme: {
        template: 'modern',
        colors: {
          widget: dataColor || '#2B8FD4',
          botBubble: '#eff6ff',
          userBubble: dataColor || '#2B8FD4',
          botText: '#1f2937',
          userText: '#ffffff',
          launcher: dataColor || '#2B8FD4',
        },
        dimensions: {
          widgetWidth: 400,
          widgetHeight: 640,
          launcherSize: 64,
          launcherRadius: 32,
          messageBubbleRadius: 16,
          inputRadius: 24,
        },
        typography: {
          fontFamily: 'system-ui, -apple-system, sans-serif',
          headerFontSize: 16,
          messageFontSize: 14,
          inputFontSize: 14,
          headerHeight: 72,
        },
        content: {
          headerSubtext: 'How can we help?',
          inputPlaceholder: 'Type your message...',
          launcherIcon: 'chat',
          showTimestamps: true,
          showVelaBranding: true,
          sendButtonStyle: 'icon',
        },
        behavior: {
          position: (dataPosition as 'bottom-right' | 'bottom-left') || 'bottom-right',
          channels: ['chat'],
        },
        branding: {
          botAvatar: null,
          botName: 'Assistant',
        },
      },
    }
    bootstrap = {
      channels: [
        {
          id: 'ai_chat',
          label: 'Chat with AI',
          description: 'Get an instant answer from our assistant',
          icon: 'bot',
          enabled: true,
        },
      ],
      autoOpen: 'ai_chat',
    }
  }

  cfg = normalizeWidgetConfig(cfg)
  loadingHost.classList.add(`vela-template-${cfg.theme.template}`)

  // Apply script-level overrides (operator can set position/color per page).
  if (dataPosition) {
    cfg.widgetPosition = dataPosition
    cfg.theme.behavior.position = dataPosition
  }
  if (dataColor) {
    cfg.widgetColor = dataColor
    cfg.theme.colors.widget = dataColor
    cfg.theme.colors.launcher = dataColor
    if (!cfg.userBubbleColor || cfg.theme.colors.userBubble === cfg.widgetColor) {
      cfg.theme.colors.userBubble = dataColor
    }
  }

  injectStyles(loadingRoot, cfg)
  renderBootLoadingState(loadingRoot, cfg)

  const host = loadingHost
  // Seed the Widget Designer settings as inline custom properties on the shadow
  // host. They inherit into the shadow tree, where the stylesheet reads them via
  // var(--vela-*, default). Setting them on the host element (rather than via a
  // :host{} rule appended to the stylesheet) guarantees the values are present
  // and inherit regardless of stylesheet parse timing.
  for (const [name, value] of widgetCssVars(cfg)) host.style.setProperty(name, value)
  const root = loadingRoot
  root.innerHTML = ''
  injectStyles(root, cfg)

  const widget = new Widget(root, cfg, bootstrap)
  widget.mount()
  if (cfg.cookieConsent?.enabled && !hasStoredCookieConsent(cfg.cookieConsent.policyVersion)) {
    mountCookieBanner(root, cfg, widget)
  }

  // Shadow DOM isolates styles, not JS events — keystrokes and clicks inside
  // the widget still bubble out to document and hit host-page listeners. When
  // the widget is previewed inside the Vela dashboard that fires the admin
  // app's own keyboard shortcuts as you type in the chat input. Stop all
  // widget-originated keyboard and click events from escaping the host.
  host.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') widget.close()
    e.stopPropagation()
  })
  host.addEventListener('keyup', (e) => e.stopPropagation())
  host.addEventListener('click', (e) => e.stopPropagation())
}

async function fetchConfig(): Promise<WidgetConfig> {
  const r = await fetch(`${apiBase}/api/v1/widget/config`, {
    method: 'GET',
    headers: { 'x-vela-key': apiKey },
  })
  if (!r.ok) throw new Error(`config http ${r.status}`)
  const body = await r.json()
  return body.data as WidgetConfig
}

async function fetchChannels(): Promise<ChannelBootstrap> {
  const r = await fetch(`${apiBase}/api/v1/widget/${encodeURIComponent(apiKey)}/channels`, {
    method: 'GET',
  })
  if (!r.ok) throw new Error(`channels http ${r.status}`)
  const body = await r.json()
  return body.data as ChannelBootstrap
}

function renderBootLoadingState(root: ShadowRoot, cfg: WidgetConfig): void {
  const wrap = document.createElement('div')
  wrap.innerHTML = `
    <div class="vela-host vela-widget vela-template-${escapeHtml(cfg.theme.template)}" data-pos="${escapeHtml(cfg.widgetPosition)}">
      <div style="display:flex;flex-direction:column;gap:8px;align-items:flex-end">
        <button class="vela-bubble" aria-label="Loading widget" disabled style="opacity:0.85;cursor:progress">
          <span style="font-size:12px;font-weight:700">...</span>
        </button>
      </div>
    </div>
  `
  root.appendChild(wrap)
}

/* ---------------------------------------------------------------------- */
/* Persistent visitor id                                                   */
/* ---------------------------------------------------------------------- */

function getVisitorId(): string {
  const KEY = 'vela_visitor_id'
  let id: string | null = null
  try { id = localStorage.getItem(KEY) } catch { id = null }
  if (!id) {
    id = `v_${randomId()}`
    try { localStorage.setItem(KEY, id) } catch { /* private mode etc. */ }
  }
  return id
}

function randomId(): string {
  // crypto.randomUUID is available in modern browsers; fall back to a
  // pseudo-random string for older ones.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return c.randomUUID().replace(/-/g, '')
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('')
}

/* ---------------------------------------------------------------------- */
/* Styles                                                                  */
/* ---------------------------------------------------------------------- */

function injectStyles(root: ShadowRoot, cfg: WidgetConfig): void {
  const color = cfg.theme.colors.widget
  const dark = darken(color, 10)
  const rgb = hexToRgb(color)
  const sheet = document.createElement('style')
  sheet.textContent = STYLE
    .replace(/__COLOR__/g, color)
    .replace(/__COLOR_DARK__/g, dark)
    .replace(/__COLOR_RGB__/g, rgb)
  root.appendChild(sheet)
}

/**
 * Build the list of CSS custom properties from the persona's Widget Designer
 * settings. These are applied as inline custom properties on the SHADOW HOST
 * element (see boot) rather than via a `:host{}` rule — inline host props are
 * the most reliable way to seed values that must inherit into the shadow tree,
 * with no dependency on stylesheet parse order. Only defined values are
 * written; anything omitted falls back to the default baked into the
 * stylesheet's `var(--x, default)` (the Modern theme), so an un-customised
 * widget still looks great. Width/height always resolve
 * (data-attr > persona > default 420/620).
 */
function normalizeWidgetConfig(cfg: WidgetConfig): WidgetConfig {
  const theme = cfg.theme
  return {
    ...cfg,
    widgetColor: theme.colors.widget,
    widgetPosition: theme.behavior.position,
    channels: theme.behavior.channels,
    avatarUrl: theme.branding.botAvatar,
    showVelaBranding: theme.content.showVelaBranding,
    widgetWidth: theme.dimensions.widgetWidth,
    widgetHeight: theme.dimensions.widgetHeight,
    launcherSize: theme.dimensions.launcherSize,
    launcherIcon: theme.content.launcherIcon,
    launcherRadius: theme.dimensions.launcherRadius,
    headerHeight: theme.typography.headerHeight,
    headerFontSize: theme.typography.headerFontSize,
    headerSubtext: theme.content.headerSubtext,
    messageFontSize: theme.typography.messageFontSize,
    messageBubbleRadius: theme.dimensions.messageBubbleRadius,
    botBubbleColor: theme.colors.botBubble,
    botTextColor: theme.colors.botText,
    userBubbleColor: theme.colors.userBubble,
    userTextColor: theme.colors.userText,
    showTimestamps: theme.content.showTimestamps,
    inputFontSize: theme.typography.inputFontSize,
    inputPlaceholder: theme.content.inputPlaceholder,
    inputRadius: theme.dimensions.inputRadius,
    sendButtonStyle: theme.content.sendButtonStyle,
    widgetFontFamily: theme.typography.fontFamily,
    personaName: theme.branding.botName || cfg.personaName,
  }
}

function widgetCssVars(cfg: WidgetConfig): Array<[string, string]> {
  const theme = cfg.theme
  const width = dataWidth ?? theme.dimensions.widgetWidth
  const height = dataHeight ?? theme.dimensions.widgetHeight
  const out: Array<[string, string]> = [
    ['--vela-width', `${width}px`],
    ['--vela-height', `${height}px`],
    ['--vela-color-widget', theme.colors.widget],
    ['--vela-color-bot-bubble', theme.colors.botBubble],
    ['--vela-color-user-bubble', theme.colors.userBubble],
    ['--vela-color-bot-text', theme.colors.botText],
    ['--vela-color-user-text', theme.colors.userText],
    ['--vela-color-launcher', theme.colors.launcher],
    ['--vela-launcher-size', `${theme.dimensions.launcherSize}px`],
    ['--vela-launcher-radius', `${theme.dimensions.launcherRadius}px`],
    ['--vela-message-radius', `${theme.dimensions.messageBubbleRadius}px`],
    ['--vela-input-radius', `${theme.dimensions.inputRadius}px`],
    ['--vela-font-family', theme.typography.fontFamily],
    ['--vela-font-header', `${theme.typography.headerFontSize}px`],
    ['--vela-font-message', `${theme.typography.messageFontSize}px`],
    ['--vela-font-input', `${theme.typography.inputFontSize}px`],
    ['--vela-header-height', `${theme.typography.headerHeight}px`],
  ]
  const px = (k: string, v: number | null | undefined) => { if (v != null) out.push([k, `${v}px`]) }
  const raw = (k: string, v: string | null | undefined) => { if (v) out.push([k, v]) }
  raw('--vela-launcher-shadow', cfg.launcherShadow)
  return out
}

const STYLE = `
:host, * { box-sizing: border-box; }

:host {
  --vela-color-brand: var(--vela-color-widget);
  --vela-color-brand-hover: __COLOR_DARK__;
  --vela-color-brand-rgb: __COLOR_RGB__;
  --vela-color-text-primary: #0f172a;
  --vela-color-text-secondary: #64748b;
  --vela-color-text-muted: #94a3b8;
  --vela-color-bg: #ffffff;
  --vela-color-surface: #f8fafc;
  --vela-color-surface-strong: #ffffff;
  --vela-color-border: #e2e8f0;
  --vela-color-success: #22c55e;
  --vela-color-warning: #eab308;
  --vela-color-neutral: #94a3b8;
  --vela-color-brand-12: rgba(__COLOR_RGB__, 0.12);
  --vela-color-brand-8: rgba(__COLOR_RGB__, 0.08);
  --vela-color-hover: rgba(0,0,0,0.03);
  --vela-color-footer: rgba(0,0,0,0.4);
  --vela-shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
  --vela-shadow-md: 0 4px 12px rgba(0,0,0,0.08);
  --vela-shadow-lg: 0 8px 32px rgba(0,0,0,0.12), 0 20px 60px rgba(0,0,0,0.06);
  --vela-transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
  --vela-transition-normal: 250ms cubic-bezier(0.4, 0, 0.2, 1);
  --vela-font-family: system-ui, -apple-system, 'Inter', sans-serif;
  --vela-heading-font-family: system-ui, -apple-system, 'Inter', sans-serif;
  --vela-heading-font-weight: 600;
  --vela-header-surface: linear-gradient(180deg, rgba(__COLOR_RGB__, 0.08) 0%, var(--vela-color-widget) 100%);
  --vela-launcher-background: var(--vela-color-widget);
  --vela-launcher-shadow: var(--vela-shadow-md);
  --vela-launcher-hover-transform: scale(1);
  --vela-launcher-hover-shadow: var(--vela-shadow-md);
  --vela-welcome-background: var(--vela-color-surface-strong);
  --vela-welcome-border: 1px solid var(--vela-color-border);
  --vela-channel-background: var(--vela-color-surface-strong);
  --vela-channel-border: 1px solid var(--vela-color-border);
  --vela-channel-hover: var(--vela-color-hover);
  --vela-channel-icon-background: var(--vela-color-brand-12);
  --vela-channel-icon-color: var(--vela-color-widget);
  --vela-avatar-background: #ffffff;
  --vela-avatar-border: 1px solid rgba(255,255,255,0.24);
  --vela-avatar-shadow: none;
  --vela-submit-background: var(--vela-color-widget);
  --vela-submit-color: #ffffff;
  --vela-submit-border: none;
  --vela-empty-icon-background: var(--vela-color-brand-12);
  --vela-empty-icon-color: var(--vela-color-widget);
}

@media (prefers-color-scheme: dark) {
  :host {
    --vela-color-text-primary: #f1f5f9;
    --vela-color-text-secondary: #94a3b8;
    --vela-color-text-muted: #64748b;
    --vela-color-bg: #0f172a;
    --vela-color-surface: #1e293b;
    --vela-color-surface-strong: #162235;
    --vela-color-border: #334155;
    --vela-color-hover: rgba(255,255,255,0.04);
    --vela-color-footer: rgba(255,255,255,0.5);
    --vela-avatar-background: #162235;
    --vela-welcome-background: #162235;
    --vela-channel-background: #162235;
  }
}

.vela-host { position: fixed; font-family: var(--vela-font-family); font-size: 14px; color: var(--vela-color-text-primary); line-height: 1.5; }
.vela-widget { color: var(--vela-color-text-primary); }
.vela-template-corporate { --vela-heading-font-family: Georgia, Charter, serif; --vela-header-surface: var(--vela-color-widget); }
.vela-template-playful { --vela-heading-font-family: 'Nunito', 'Quicksand', system-ui, sans-serif; }
.vela-template-minimal { --vela-heading-font-family: 'Inter', system-ui, sans-serif; --vela-header-surface: var(--vela-color-widget); }
.vela-host[data-pos="bottom-right"] { right: 20px; bottom: 20px; }
.vela-host[data-pos="bottom-left"] { left: 20px; bottom: 20px; }
.vela-bubble { width: 64px; height: 64px; border-radius: 9999px; background: var(--vela-launcher-background); color: #ffffff; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: var(--vela-launcher-shadow); transition: transform var(--vela-transition-fast), box-shadow var(--vela-transition-fast), background var(--vela-transition-fast); position: relative; }
.vela-bubble:hover { transform: var(--vela-launcher-hover-transform); box-shadow: var(--vela-launcher-hover-shadow); }
.vela-bubble:focus-visible { outline: 2px solid rgba(__COLOR_RGB__, 0.8); outline-offset: 2px; }
@keyframes vela-pulse { 0% { box-shadow: 0 0 0 0 rgba(__COLOR_RGB__, 0.12); } 70% { box-shadow: 0 0 0 32px rgba(__COLOR_RGB__, 0); } 100% { box-shadow: 0 0 0 0 rgba(__COLOR_RGB__, 0); } }
.vela-bubble.vela-pulsing { animation: vela-pulse 1.8s ease-out 3; }
.vela-badge { position: absolute; top: 4px; right: 4px; min-width: 20px; height: 20px; padding: 0 4px; background: #ef4444; color: #ffffff; border-radius: 9999px; font-size: 12px; font-weight: 600; line-height: 20px; text-align: center; border: 1px solid rgba(255,255,255,0.92); }
.vela-toast { background: #0f172a; color: #ffffff; padding: 12px; border-radius: 12px; font-size: 12px; line-height: 1.4; box-shadow: var(--vela-shadow-md); }
.vela-toast-floating { position: absolute; bottom: 80px; right: 0; max-width: 220px; }
.vela-toast-inline { margin-top: 8px; }
.vela-teaser { position: absolute; bottom: 80px; right: 0; width: 260px; background: var(--vela-color-surface-strong); border: 1px solid var(--vela-color-border); border-radius: 12px; box-shadow: var(--vela-shadow-md); padding: 16px; font-size: 12px; color: var(--vela-color-text-primary); cursor: pointer; animation: vela-rise 0.3s ease-out; }
.vela-host[data-pos="bottom-left"] .vela-teaser { right: auto; left: 0; }
.vela-teaser-close { position: absolute; top: 8px; right: 8px; width: 20px; height: 20px; border: none; background: transparent; cursor: pointer; color: var(--vela-color-text-secondary); font-size: 12px; line-height: 20px; }
.vela-card { position: absolute; bottom: 80px; width: 400px; max-width: calc(100vw - 40px); height: 640px; max-height: calc(100vh - 100px); background: var(--vela-color-bg); border-radius: 16px; box-shadow: var(--vela-shadow-lg); display: flex; flex-direction: column; overflow: hidden; border: 1px solid var(--vela-color-border); }
.vela-card-animate { animation: vela-rise 0.3s ease-out; }
.vela-host[data-pos="bottom-right"] .vela-card { right: 0; }
.vela-host[data-pos="bottom-left"] .vela-card { left: 0; }
@keyframes vela-rise { 0% { opacity: 0; transform: translateY(20px); } 100% { opacity: 1; transform: translateY(0); } }
.vela-header { background: var(--vela-header-surface); color: #ffffff; min-height: 88px; padding: 16px 20px; display: flex; align-items: center; gap: 12px; }
.vela-header h2 { margin: 0; font-size: 22px; font-family: var(--vela-heading-font-family); font-weight: 600; line-height: 1.2; }
.vela-header .vela-sub { font-size: 12px; font-weight: 500; line-height: 1.4; opacity: 0.96; }
.vela-header-text { flex: 1; min-width: 0; }
.vela-avatar { flex-shrink: 0; border-radius: 9999px; overflow: hidden; background: var(--vela-avatar-background); color: var(--vela-color-widget); display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600; border: var(--vela-avatar-border); box-shadow: var(--vela-avatar-shadow); }
.vela-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.vela-status-line { display: inline-flex; align-items: center; gap: 8px; }
.vela-status-dot { width: 8px; height: 8px; border-radius: 9999px; display: inline-block; flex-shrink: 0; }
.vela-status-dot-online { background: var(--vela-color-success); }
.vela-status-dot-away { background: var(--vela-color-warning); }
.vela-status-dot-offline { background: var(--vela-color-neutral); }
.vela-iconbtn { width: 32px; height: 32px; border-radius: 8px; border: none; background: transparent; color: #ffffff; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background var(--vela-transition-fast); }
.vela-iconbtn:hover { background: rgba(255,255,255,0.15); }
.vela-iconbtn:focus-visible { outline: 2px solid rgba(__COLOR_RGB__, 0.8); outline-offset: 2px; }
.vela-body { flex: 1; min-height: 0; overflow-y: auto; background: var(--vela-color-bg); }
.vela-footer-attribution { display: flex; align-items: center; justify-content: center; padding: 12px; font-size: 11px; font-weight: 500; line-height: 1.4; color: var(--vela-color-footer); text-decoration: none; border-top: 1px solid var(--vela-color-border); margin-top: auto; }
.vela-picker { padding: 20px; display: flex; flex-direction: column; }
.vela-welcome { padding: 20px; margin-bottom: 16px; background: var(--vela-welcome-background); border: var(--vela-welcome-border); border-radius: 12px; min-height: 120px; }
.vela-welcome-icon { width: 48px; height: 48px; color: var(--vela-color-widget); margin-bottom: 16px; }
.vela-welcome-icon svg { width: 48px; height: 48px; display: block; }
.vela-welcome-text strong { display: block; font-size: 18px; font-family: var(--vela-heading-font-family); font-weight: 600; line-height: 1.2; color: var(--vela-color-text-primary); margin-bottom: 8px; }
.vela-welcome-text p { margin: 0; font-size: 14px; font-weight: 400; line-height: 1.5; color: var(--vela-color-text-secondary); }
.vela-channel { display: flex; align-items: center; gap: 12px; width: 100%; min-height: 64px; padding: 16px; background: var(--vela-channel-background); border: var(--vela-channel-border); border-radius: 12px; cursor: pointer; text-align: left; transition: background var(--vela-transition-fast); }
.vela-channel + .vela-channel { margin-top: 8px; }
.vela-channel:hover { background: var(--vela-channel-hover); }
.vela-channel-icon { width: 40px; height: 40px; border-radius: 9999px; background: var(--vela-channel-icon-background); color: var(--vela-channel-icon-color); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.vela-channel-text { flex: 1; min-width: 0; }
.vela-channel-title { font-size: 14px; font-family: var(--vela-heading-font-family); font-weight: 600; line-height: 1.5; color: var(--vela-color-text-primary); }
.vela-channel-sub { margin-top: 4px; font-size: 12px; font-weight: 400; line-height: 1.4; color: var(--vela-color-text-secondary); }
.vela-messages { padding: 20px; display: flex; flex-direction: column; gap: 8px; }
.vela-msg-row { display: flex; align-items: flex-end; gap: 8px; max-width: 88%; animation: vela-msg-in 0.2s ease-out; }
.vela-msg-row.user { align-self: flex-end; justify-content: flex-end; }
.vela-msg-row.bot { align-self: flex-start; }
.vela-msg-stack { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.vela-msg-bubble { padding: 12px 16px; font-size: 14px; font-weight: 400; line-height: 1.5; white-space: pre-wrap; word-wrap: break-word; max-width: min(75vw, 280px); }
.vela-msg-row.bot .vela-msg-bubble { background: var(--vela-color-bot-bubble, var(--vela-color-surface-strong)); color: var(--vela-color-bot-text, var(--vela-color-text-primary)); border-radius: 16px; border: 1px solid var(--vela-color-border); }
.vela-msg-row.user .vela-msg-bubble { background: var(--vela-color-user-bubble, var(--vela-color-widget)); color: var(--vela-color-user-text, #ffffff); border-radius: 16px; border: none; }
.vela-msg-bubble + .vela-msg-bubble { margin-top: 4px; }
.vela-ts { font-size: 12px; font-weight: 400; line-height: 1.4; color: var(--vela-color-text-secondary); }
.vela-msg-row.user .vela-ts { text-align: right; }
.vela-msg-row.bot .vela-avatar { width: 32px !important; height: 32px !important; align-self: flex-end; margin-bottom: 20px; }
@keyframes vela-msg-in { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }
.vela-filler { padding: 4px 8px; font-size: 12px; font-weight: 400; line-height: 1.4; color: var(--vela-color-text-secondary); }
.vela-typing { display: inline-flex; gap: 8px; align-items: center; background: var(--vela-color-surface-strong); padding: 12px 16px; border-radius: 16px; border: 1px solid var(--vela-color-border); }
.vela-typing span { width: 8px; height: 8px; border-radius: 9999px; background: var(--vela-color-text-secondary); animation: vela-bounce 1.2s infinite ease-in-out both; }
.vela-typing span:nth-child(2) { animation-delay: -0.16s; }
.vela-typing span:nth-child(3) { animation-delay: 0s; }
.vela-typing span:nth-child(1) { animation-delay: -0.32s; }
@keyframes vela-bounce { 0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; } 40% { transform: scale(1); opacity: 1; } }
.vela-live-banner { margin: 8px 20px; padding: 12px 16px; border-radius: 12px; border: 1px solid var(--vela-color-border); background: var(--vela-color-surface); color: var(--vela-color-text-primary); font-size: 12px; line-height: 1.4; display: flex; align-items: center; gap: 8px; }
.vela-live-banner-avatar { width: 32px; height: 32px; border-radius: 9999px; background: var(--vela-color-widget); color: #ffffff; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; }
.vela-live-banner-text strong { display: block; font-size: 12px; font-weight: 600; line-height: 1.4; }
.vela-live-banner-text span { display: block; font-size: 12px; font-weight: 400; line-height: 1.4; color: var(--vela-color-text-secondary); }
.vela-input { border-top: 1px solid var(--vela-color-border); padding: 16px 20px calc(16px + env(safe-area-inset-bottom, 0px)); display: flex; gap: 8px; align-items: flex-end; background: rgba(255,255,255,0.92); backdrop-filter: blur(12px); }
.vela-input textarea { flex: 1; border: 1px solid var(--vela-color-border); outline: none; resize: none; font: inherit; font-size: 14px; font-weight: 400; line-height: 1.5; background: var(--vela-color-surface-strong); padding: 12px 16px; min-height: 48px; max-height: 120px; overflow-y: hidden; scrollbar-width: none; -ms-overflow-style: none; color: var(--vela-color-text-primary); border-radius: 12px; }
.vela-input textarea:focus { border-color: rgba(__COLOR_RGB__, 0.4); box-shadow: 0 0 0 4px rgba(__COLOR_RGB__, 0.08); }
.vela-input textarea::-webkit-scrollbar { display: none; }
.vela-input button { min-width: 40px; height: 40px; padding: 0 12px; border-radius: 8px; background: var(--vela-submit-background); color: var(--vela-submit-color); border: var(--vela-submit-border); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 4px; font: inherit; font-size: 14px; font-weight: 500; flex-shrink: 0; }
.vela-input button:hover:not(:disabled) { opacity: 0.96; }
.vela-input button:disabled { opacity: 0.4; cursor: not-allowed; }
.vela-link-button { width: 100%; border: none; background: transparent; padding: 4px 20px 0; text-align: center; font: inherit; font-size: 12px; font-weight: 400; line-height: 1.4; color: var(--vela-color-text-secondary); cursor: pointer; }
.vela-form { padding: 20px; display: flex; flex-direction: column; gap: 12px; }
.vela-field { display: flex; flex-direction: column; gap: 4px; }
.vela-field label { font-size: 12px; font-weight: 500; line-height: 1.4; color: var(--vela-color-text-secondary); }
.vela-field input, .vela-field textarea { font: inherit; font-size: 14px; font-weight: 400; line-height: 1.5; border: 1px solid var(--vela-color-border); border-radius: 12px; padding: 12px 16px; outline: none; color: var(--vela-color-text-primary); background: var(--vela-color-surface-strong); }
.vela-field input:focus, .vela-field textarea:focus { border-color: rgba(__COLOR_RGB__, 0.4); box-shadow: 0 0 0 4px rgba(__COLOR_RGB__, 0.08); }
.vela-field textarea { resize: vertical; min-height: 96px; }
.vela-form button[type="submit"] { margin-top: 4px; background: var(--vela-submit-background); color: var(--vela-submit-color); border: var(--vela-submit-border); padding: 12px 16px; border-radius: 8px; font-size: 14px; font-weight: 500; line-height: 1.5; cursor: pointer; }
.vela-form button[type="submit"]:disabled { opacity: 0.6; cursor: progress; }
.vela-form-error { background: #fef2f2; color: #991b1b; border-radius: 8px; padding: 8px 12px; font-size: 12px; line-height: 1.4; }
.vela-thanks { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 20px; text-align: center; }
.vela-check { width: 48px; height: 48px; border-radius: 9999px; background: rgba(34,197,94,0.12); color: #16a34a; display: flex; align-items: center; justify-content: center; }
.vela-thanks h3 { margin: 0; font-size: 18px; font-family: var(--vela-heading-font-family); font-weight: 600; line-height: 1.2; color: var(--vela-color-text-primary); }
.vela-thanks p { margin: 0; max-width: 280px; font-size: 14px; font-weight: 400; line-height: 1.5; color: var(--vela-color-text-secondary); }
.vela-thanks button { background: transparent; border: none; color: var(--vela-color-widget); font: inherit; font-size: 12px; font-weight: 500; line-height: 1.4; cursor: pointer; text-decoration: underline; }
.vela-empty { display: flex; flex-direction: column; align-items: flex-start; gap: 12px; padding: 20px; background: var(--vela-color-surface-strong); border: 1px solid var(--vela-color-border); border-radius: 12px; }
.vela-empty-icon { width: 40px; height: 40px; border-radius: 12px; background: var(--vela-empty-icon-background); color: var(--vela-empty-icon-color); display: flex; align-items: center; justify-content: center; }
.vela-empty-title { font-size: 18px; font-family: var(--vela-heading-font-family); font-weight: 600; line-height: 1.2; color: var(--vela-color-text-primary); }
.vela-empty-sub { font-size: 14px; font-weight: 400; line-height: 1.5; color: var(--vela-color-text-secondary); }
.vela-consent, .vela-csat-overlay { position: absolute; inset: 0; z-index: 6; background: rgba(255,255,255,0.92); display: flex; align-items: center; justify-content: center; padding: 20px; }
.vela-consent-box, .vela-csat-card { width: 100%; max-width: 320px; background: var(--vela-color-surface-strong); border: 1px solid var(--vela-color-border); border-radius: 16px; padding: 20px; box-shadow: var(--vela-shadow-lg); }
.vela-consent-box p { margin: 0 0 12px; font-size: 14px; font-weight: 400; line-height: 1.5; color: var(--vela-color-text-secondary); }
.vela-consent-box a, .vela-cookie-link { color: var(--vela-color-widget); }
.vela-consent-btn, .vela-csat-submit, .vela-csat-skip, .vela-cookie-actions button { border-radius: 8px; padding: 12px 16px; font: inherit; font-size: 14px; font-weight: 500; line-height: 1.5; cursor: pointer; }
.vela-consent-btn, .vela-csat-submit, .vela-cookie-accept { color: #ffffff; border: none; }
.vela-csat-title { margin: 0 0 12px; font-size: 18px; font-weight: 600; line-height: 1.2; color: var(--vela-color-text-primary); }
.vela-csat-stars { display: flex; justify-content: center; gap: 8px; margin-bottom: 12px; }
.vela-star { background: none; border: 0; font-size: 28px; line-height: 1.2; cursor: pointer; color: #d1d5db; padding: 0; }
.vela-csat-comment { width: 100%; box-sizing: border-box; border: 1px solid var(--vela-color-border); border-radius: 12px; padding: 12px 16px; font: inherit; font-size: 14px; line-height: 1.5; resize: none; margin-bottom: 12px; color: var(--vela-color-text-primary); }
.vela-csat-actions { display: flex; gap: 8px; }
.vela-csat-submit:disabled { opacity: 0.6; cursor: progress; }
.vela-csat-skip, .vela-cookie-save { border: 1px solid var(--vela-color-border); background: var(--vela-color-surface); color: var(--vela-color-text-primary); }
.vela-cookie { position: fixed; bottom: 20px; width: 320px; max-width: calc(100vw - 40px); background: var(--vela-color-surface-strong); border: 1px solid var(--vela-color-border); border-radius: 16px; box-shadow: var(--vela-shadow-lg); padding: 16px; font-family: var(--vela-font-family); font-size: 14px; color: var(--vela-color-text-primary); z-index: 2147483601; }
.vela-host[data-pos="bottom-right"] ~ .vela-cookie, .vela-cookie { right: 20px; }
.vela-cookie h3 { margin: 0 0 8px; font-size: 18px; font-weight: 600; line-height: 1.2; color: var(--vela-color-text-primary); }
.vela-cookie p { margin: 0 0 12px; font-size: 12px; line-height: 1.4; color: var(--vela-color-text-secondary); }
.vela-cookie label { display: flex; align-items: center; gap: 8px; padding: 4px 0; font-size: 12px; line-height: 1.4; color: var(--vela-color-text-primary); }
.vela-cookie label input[disabled] { opacity: 0.6; }
.vela-cookie-actions { display: flex; gap: 8px; margin-top: 12px; }
.vela-cookie-link { display: block; margin-top: 8px; font-size: 12px; line-height: 1.4; text-decoration: underline; }
.vela-escalation { margin: 8px 20px; padding: 12px 16px; background: #fffbeb; border: 1px solid #fef3c7; border-radius: 12px; font-size: 12px; line-height: 1.4; color: #92400e; display: flex; align-items: center; gap: 8px; }
.vela-spinner { width: 16px; height: 16px; border-radius: 9999px; border: 2px solid #fbbf24; border-top-color: transparent; animation: vela-spin 0.8s linear infinite; flex-shrink: 0; }
@keyframes vela-spin { to { transform: rotate(360deg); } }
@media (max-width: 640px) { .vela-host[data-pos="bottom-right"] { right: 12px; bottom: calc(12px + env(safe-area-inset-bottom, 0px)); } .vela-host[data-pos="bottom-left"] { left: 12px; bottom: calc(12px + env(safe-area-inset-bottom, 0px)); } .vela-card { bottom: calc(80px + env(safe-area-inset-bottom, 0px)); width: calc(100vw - 24px); max-width: calc(100vw - 24px); height: calc(100vh - 96px); max-height: calc(100vh - 96px); border-bottom-left-radius: 0; border-bottom-right-radius: 0; } .vela-msg-bubble { max-width: min(78vw, 320px); } }
@media (prefers-reduced-motion: reduce) { .vela-bubble, .vela-channel, .vela-iconbtn, .vela-input button, .vela-card-animate, .vela-msg-row, .vela-bubble.vela-pulsing, .vela-teaser, .vela-spinner, .vela-typing span { animation: none !important; transition: none !important; transform: none !important; } }
`

/* ---------------------------------------------------------------------- */
/* Widget controller                                                       */
/* ---------------------------------------------------------------------- */

class Widget {
  private view: View = 'closed'
  private chat: ChatMsg[] = []
  private sessionId: string | null = null
  private typing = false
  private filler: string | null = null
  private escalated = false
  private hostEl!: HTMLDivElement
  private container!: HTMLDivElement
  private teaserDismissed = false
  private proactiveTimer: number | null = null

  // Live-chat state — inactive until the bot escalates (or the visitor
  // clicks "Talk to a human"). Once we have a liveChatId, every send goes
  // through /live-chat/message instead of /message and agent replies arrive
  // via Socket.io.
  private liveChatId: string | null = null
  private liveChatStatus: 'none' | 'waiting' | 'active' | 'resolved' = 'none'
  private queuePosition: number | null = null
  private agentName: string | null = null
  private agentInitials: string | null = null
  private agentTyping = false
  private socket: Socket | null = null

  // True for exactly one render — the first after a closed → open transition.
  // Drives the vela-rise animation so it only plays when the card first
  // appears, not on every re-render while the card is already visible.
  private animateCardNext = false

  // CSAT is offered once per session (on bot farewell / end / agent resolve).
  private csatShown = false
  private readonly availableChannels: WidgetChannel[]
  private readonly autoOpenChannel: PublicChannelId | null
  private toastMessage: string | null = null
  private toastTimer: number | null = null

  constructor(
    private readonly root: ShadowRoot,
    private readonly cfg: WidgetConfig,
    bootstrap: ChannelBootstrap,
  ) {
    this.availableChannels = bootstrap.channels.filter((channel) => channel.enabled)
    this.autoOpenChannel = bootstrap.autoOpen
  }

  mount(): void {
    this.hostEl = document.createElement('div')
    this.hostEl.className = `vela-host vela-widget vela-template-${this.cfg.theme.template}`
    this.hostEl.dataset.pos = this.cfg.widgetPosition
    this.root.appendChild(this.hostEl)
    this.container = document.createElement('div')
    this.hostEl.appendChild(this.container)

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.view !== 'closed') {
        this.close()
      }
    })

    try {
      this.teaserDismissed = sessionStorage.getItem('vela_proactive_dismissed') === '1'
    } catch { this.teaserDismissed = false }

    if (this.cfg.proactiveEnabled && !this.teaserDismissed) {
      this.proactiveTimer = window.setTimeout(() => this.render(), this.cfg.proactiveDelay * 1000)
    }

    this.render()
  }

  /* ---------- View transitions ---------- */

  private open(view: View): void {
    if (this.view === 'closed') this.animateCardNext = true
    this.view = view
    if (this.proactiveTimer) { clearTimeout(this.proactiveTimer); this.proactiveTimer = null }
    this.render()
  }

  close(): void {
    this.view = 'closed'
    this.render()
  }

  private dismissTeaser(): void {
    this.teaserDismissed = true
    try { sessionStorage.setItem('vela_proactive_dismissed', '1') } catch { /* ignore */ }
    if (this.proactiveTimer) { clearTimeout(this.proactiveTimer); this.proactiveTimer = null }
    this.render()
  }

  /* ---------- Render ---------- */

  private render(): void {
    this.container.innerHTML = ''
    if (this.view === 'closed') {
      this.renderBubble()
    } else {
      this.renderCard()
    }
  }

  private flash(message: string): void {
    this.toastMessage = message
    if (this.toastTimer) window.clearTimeout(this.toastTimer)
    this.toastTimer = window.setTimeout(() => {
      this.toastMessage = null
      this.toastTimer = null
      this.render()
    }, 2000)
    this.render()
  }

  private channelById(id: PublicChannelId): WidgetChannel | null {
    return this.availableChannels.find((channel) => channel.id === id) ?? null
  }

  private async openDefaultEntry(): Promise<void> {
    if (this.autoOpenChannel === 'ai_chat') {
      await this.startChat()
      return
    }
    if (this.autoOpenChannel === 'whatsapp') {
      this.handleExternalChannel('whatsapp')
      return
    }
    this.open('picker')
  }

  private renderBubble(): void {
    const wrap = document.createElement('div')

    if (this.cfg.proactiveEnabled && !this.teaserDismissed && this.proactiveTimer === null) {
      // Timer already fired → show teaser.
      const t = document.createElement('div')
      t.className = 'vela-teaser'
      t.textContent = this.cfg.proactiveMessage
      t.setAttribute('role', 'dialog')
      t.addEventListener('click', () => this.open('picker'))
      const x = document.createElement('button')
      x.className = 'vela-teaser-close'
      x.textContent = '×'
      x.setAttribute('aria-label', 'Dismiss')
      x.addEventListener('click', (e) => { e.stopPropagation(); this.dismissTeaser() })
      t.appendChild(x)
      wrap.appendChild(t)
    }

    const btn = document.createElement('button')
    btn.className = 'vela-bubble vela-pulsing'
    btn.setAttribute('aria-label', 'Open chat')
    const iconKey = this.cfg.launcherIcon as keyof typeof ICONS
    btn.innerHTML = ICONS[iconKey] ?? ICONS.chat
    btn.addEventListener('click', () => { void this.openDefaultEntry() })
    btn.addEventListener('animationend', () => btn.classList.remove('vela-pulsing'))
    wrap.appendChild(btn)

    if (this.toastMessage) {
      const toast = document.createElement('div')
      toast.className = 'vela-toast vela-toast-floating'
      toast.textContent = this.toastMessage
      wrap.appendChild(toast)
    }

    this.container.appendChild(wrap)
  }

  private renderCard(): void {
    const card = document.createElement('div')
    card.className = this.animateCardNext ? 'vela-card vela-card-animate' : 'vela-card'
    this.animateCardNext = false
    card.setAttribute('role', 'dialog')
    card.setAttribute('aria-modal', 'true')

    const header = this.renderHeader()
    card.appendChild(header)

    const body = document.createElement('div')
    body.className = 'vela-body'
    card.appendChild(body)

    if (this.view === 'picker')        this.renderPicker(body)
    else if (this.view === 'chat')     this.renderChat(card, body)
    else if (this.view === 'contact_form') this.renderForm(body)
    else if (this.view === 'thanks')   this.renderThanks(body)

    // "Powered by Vela" — hidden when the tenant has branding turned off.
    if (this.cfg.showVelaBranding !== false) {
      const foot = document.createElement('a')
      foot.className = 'vela-footer-attribution'
      foot.href = 'https://vela.altream.com/signup'
      foot.target = '_blank'
      foot.rel = 'noopener noreferrer'
      // innerHTML is safe here — fully static content, no user input.
      foot.textContent = 'Powered by Vela'
      card.appendChild(foot)
    }

    this.container.appendChild(card)

    // First-visit PDPA/GDPR consent overlay — purely presentational; it sits on
    // top of the panel and never touches the chat/send pipeline.
    this.maybeRenderConsent(card)
  }

  private consentKey(): string {
    return `vela_consent_${apiKey}`
  }

  private maybeRenderConsent(card: HTMLElement): void {
    const c = this.cfg.consent
    if (!c?.enabled) return
    try { if (localStorage.getItem(this.consentKey()) === 'true') return } catch { /* no storage */ }

    const overlay = document.createElement('div')
    overlay.className = 'vela-consent'
    const policyLink = c.privacyPolicyUrl
      ? ` <a href="${escapeHtml(c.privacyPolicyUrl)}" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.`
      : ''
    const box = document.createElement('div')
    box.className = 'vela-consent-box'
    box.innerHTML = `<p>${escapeHtml(c.text)}${policyLink}</p>`
    const agree = document.createElement('button')
    agree.className = 'vela-consent-btn'
    agree.style.background = 'var(--vela-color-widget)'
    agree.textContent = c.buttonText || 'I agree'
    agree.addEventListener('click', () => {
      try { localStorage.setItem(this.consentKey(), 'true') } catch { /* ignore */ }
      overlay.remove()
    })
    box.appendChild(agree)
    overlay.appendChild(box)
    card.appendChild(overlay)
  }

  /** Bot avatar: persona/tenant image, else first letter on a white circle. */
  private avatarEl(size: number): HTMLElement {
    const wrap = document.createElement('div')
    wrap.className = 'vela-avatar'
    wrap.style.width = `${size}px`
    wrap.style.height = `${size}px`
    if (this.cfg.avatarUrl) {
      const img = document.createElement('img')
      img.src = this.cfg.avatarUrl
      img.alt = ''
      img.onerror = () => { img.style.display = 'none'; wrap.textContent = (this.cfg.personaName || '?').charAt(0).toUpperCase() }
      wrap.appendChild(img)
    } else {
      wrap.textContent = (this.cfg.personaName || '?').charAt(0).toUpperCase()
    }
    return wrap
  }

  /**
   * Header status line driven by the tenant's live availability (agents online
   * OR within business hours). Returns an HTML string with a coloured dot, or
   * '' when no business-hours config was returned (callers fall back to copy).
   */
  private availabilityLine(): string {
    const bh = this.cfg.businessHours
    if (!bh) return ''
    const line = (klass: string, label: string) =>
      `<span class="vela-status-line"><span class="vela-status-dot ${klass}"></span><span>${escapeHtml(label)}</span></span>`
    if (bh.online) {
      return line('vela-status-dot-online', 'Typically replies within a few minutes')
    }
    if (bh.withinHours) {
      return line('vela-status-dot-away', "We're stepping away - back shortly")
    }
    const next = bh.nextOpen
    const suffix = !next
      ? ''
      : /tomorrow/i.test(next)
        ? ' tomorrow morning'
        : ` ${next}`
    return line('vela-status-dot-offline', `We'll reply when we're back${suffix}`)
  }

  private renderHeader(): HTMLElement {
    const header = document.createElement('div')
    header.className = 'vela-header'

    const showBack = this.shouldShowBackButton()
    if (showBack) {
      const back = document.createElement('button')
      back.className = 'vela-iconbtn'
      back.innerHTML = ICONS.back
      back.setAttribute('aria-label', 'Back')
      back.addEventListener('click', () => this.handleBack())
      header.appendChild(back)
    }

    // Bot avatar (image, or first-letter fallback) — cosmetic only.
    if (!showBack) header.appendChild(this.avatarEl(40))

    const text = document.createElement('div')
    text.className = 'vela-header-text'
    const avail = this.availabilityLine()
    if (this.view === 'chat') {
      text.innerHTML = `<h2>${escapeHtml(this.cfg.personaName)}</h2><div class="vela-sub">${avail}</div>`
    } else if (this.view === 'contact_form') {
      text.innerHTML = `<h2>Send us a message</h2><div class="vela-sub">We'll reply to your email</div>`
    } else if (this.view === 'thanks') {
      text.innerHTML = `<h2>Thanks!</h2>`
    } else {
      const sub = this.cfg.headerSubtext || 'How can we help?'
      text.innerHTML = `<h2>Hi! How can we help?</h2><div class="vela-sub">${avail || escapeHtml(sub)}</div>`
    }
    header.appendChild(text)

    const close = document.createElement('button')
    close.className = 'vela-iconbtn'
    close.innerHTML = ICONS.close
    close.setAttribute('aria-label', 'Close')
    close.addEventListener('click', () => this.close())
    header.appendChild(close)

    return header
  }

  private shouldShowBackButton(): boolean {
    if (this.view === 'contact_form') return true
    if (this.view !== 'chat') return false
    return !(this.autoOpenChannel === 'ai_chat' && this.availableChannels.length === 1)
  }

  private handleBack(): void {
    if (this.view === 'chat' && this.autoOpenChannel === 'ai_chat' && this.availableChannels.length === 1) {
      this.close()
      return
    }
    this.open('picker')
  }

  /* ---------- Picker ---------- */

  private renderPicker(body: HTMLElement): void {
    const list = document.createElement('div')
    list.className = 'vela-picker'

    // Welcome card — purely presentational, sits above the channel options.
    const welcome = document.createElement('div')
    welcome.className = 'vela-welcome'
    const welcomeText = this.cfg.headerSubtext
      || "We're here to help. Chat with our assistant or leave us a message."
    welcome.innerHTML = `
      <div class="vela-welcome-icon">${ICONS.welcomeMark}</div>
      <div class="vela-welcome-text">
        <strong>Welcome to ${escapeHtml(this.cfg.tenantName || 'us')}</strong>
        <p>${escapeHtml(welcomeText)}</p>
      </div>`
    list.appendChild(welcome)

    for (const channel of this.availableChannels) {
      list.appendChild(this.makeChannel({
        title: channel.label,
        sub: channel.description,
        icon: this.iconForChannel(channel.id),
        iconClass: channel.id === 'whatsapp' ? 'vela-wa' : undefined,
        onClick: () => this.handleChannelSelection(channel.id),
      }))
    }

    if (this.toastMessage) {
      const toast = document.createElement('div')
      toast.className = 'vela-toast vela-toast-inline'
      toast.textContent = this.toastMessage
      list.appendChild(toast)
    }

    body.appendChild(list)
  }

  private iconForChannel(id: PublicChannelId): string {
    if (id === 'ai_chat') return ICONS.aiChat
    if (id === 'contact_form') return ICONS.doc
    if (id === 'email') return ICONS.mailOpen
    if (id === 'phone') return ICONS.phone
    return ICONS.whatsapp
  }

  private handleChannelSelection(id: PublicChannelId): void {
    if (id === 'ai_chat') {
      void this.startChat()
      return
    }
    if (id === 'contact_form') {
      this.open('contact_form')
      return
    }
    this.handleExternalChannel(id)
  }

  private handleExternalChannel(id: Extract<PublicChannelId, 'whatsapp' | 'email' | 'phone'>): void {
    const channel = this.channelById(id)
    if (!channel) return
    if (id === 'whatsapp' && channel.waNumber) {
      const target = channel.waNumber.replace(/^\+/, '').replace(/[^\d]/g, '')
      const text = encodeURIComponent(channel.prefilledMessage ?? 'Hi, I need help with...')
      window.open(`https://wa.me/${target}?text=${text}`, '_blank', 'noopener')
      this.flash('Opening WhatsApp...')
      return
    }
    if (id === 'email' && channel.address) {
      window.open(`mailto:${channel.address}`, '_blank', 'noopener')
      this.flash('Opening email...')
      return
    }
    if (id === 'phone' && channel.number) {
      const phone = channel.number
      const isTouch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
      if (isTouch) {
        window.open(`tel:${phone}`, '_self')
        return
      }
      void navigator.clipboard.writeText(phone).then(() => {
        this.flash('Number copied!')
      }).catch(() => {
        this.flash(phone)
      })
    }
  }

  private makeChannel(opts: {
    title: string; sub: string; icon: string; iconClass?: string;
    onClick: () => void;
  }): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.className = 'vela-channel'
    btn.innerHTML = `
      <span class="vela-channel-icon ${opts.iconClass ?? ''}">${opts.icon}</span>
      <span class="vela-channel-text">
        <div class="vela-channel-title">${escapeHtml(opts.title)}</div>
        <div class="vela-channel-sub">${escapeHtml(opts.sub)}</div>
      </span>
    `
    btn.addEventListener('click', opts.onClick)
    return btn
  }

  /* ---------- Chat ---------- */

  private async startChat(): Promise<void> {
    this.open('chat')
    if (this.sessionId) return
    try {
      const session = await this.startSession()
      this.sessionId = session.sessionId
      this.chat.push({
        who: 'bot',
        bubbles: [session.greeting || this.cfg.greeting],
        ts: new Date(),
      })
      this.render()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[vela-widget] session start failed', err)
    }
  }

  private async startSession(): Promise<SessionResp> {
    const r = await fetch(`${apiBase}/api/v1/widget/session`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-vela-key': apiKey },
      body: JSON.stringify({
        visitorId: getVisitorId(),
        channel: 'widget',
        personaId: this.cfg.personaId,
      }),
    })
    if (!r.ok) throw new Error(`session http ${r.status}`)
    const body = await r.json()
    return body.data as SessionResp
  }

  private renderChat(card: HTMLElement, body: HTMLElement): void {
    // Clicking anywhere on the chat card (other than an interactive element)
    // sends focus back to the composer — so typing always lands in the right
    // place even if a stray click took focus elsewhere on the dashboard.
    card.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null
      if (t && t.closest('button, textarea, input, a')) return
      const ta = this.root.querySelector('textarea') as HTMLTextAreaElement | null
      ta?.focus()
    })

    const msgs = document.createElement('div')
    msgs.className = 'vela-messages'
    body.appendChild(msgs)

    if (this.chat.length === 0 && !this.typing && !this.agentTyping) {
      const empty = document.createElement('div')
      empty.className = 'vela-empty'
      empty.innerHTML = `
        <div class="vela-empty-icon">${ICONS.chat}</div>
        <div class="vela-empty-title">Start the conversation</div>
        <div class="vela-empty-sub">Ask a question, request help, or describe what you need. We’ll reply here in real time.</div>
      `
      msgs.appendChild(empty)
    }

    for (const m of this.chat) {
      const row = document.createElement('div')
      row.className = `vela-msg-row ${m.who}`
      if (m.who === 'bot') {
        row.appendChild(this.avatarEl(28))
      }
      const stack = document.createElement('div')
      stack.className = 'vela-msg-stack'
      for (const b of m.bubbles) {
        const bubble = document.createElement('div')
        bubble.className = 'vela-msg-bubble'
        bubble.textContent = b
        stack.appendChild(bubble)
      }
      if (this.cfg.showTimestamps !== false) {
        const ts = document.createElement('div')
        ts.className = 'vela-ts'
        ts.textContent = formatTime(m.ts)
        stack.appendChild(ts)
      }
      row.appendChild(stack)
      msgs.appendChild(row)
    }

    if (this.typing || this.agentTyping) {
      const row = document.createElement('div')
      row.className = 'vela-msg-row bot'
      row.appendChild(this.avatarEl(28))
      const stack = document.createElement('div')
      stack.className = 'vela-msg-stack'
      if (this.filler) {
        const f = document.createElement('div')
        f.className = 'vela-filler'
        f.textContent = this.filler
        stack.appendChild(f)
      }
      if (this.agentTyping && this.agentName) {
        const f = document.createElement('div')
        f.className = 'vela-filler'
        f.textContent = `${this.agentName} is typing…`
        stack.appendChild(f)
      }
      const dots = document.createElement('div')
      dots.className = 'vela-typing'
      dots.innerHTML = '<span></span><span></span><span></span>'
      stack.appendChild(dots)
      row.appendChild(stack)
      msgs.appendChild(row)
    }

    // Live-chat queue banner — shown while the visitor is waiting.
    if (this.liveChatStatus === 'waiting') {
      const banner = document.createElement('div')
      banner.className = 'vela-escalation'
      const pos = this.queuePosition ? `#${this.queuePosition} in queue` : 'Finding an agent'
      const eta = this.queuePosition
        ? `Estimated wait: ~${Math.max(1, this.queuePosition * 2)} minutes`
        : 'We’ll be right with you'
      banner.innerHTML = `<div class="vela-spinner"></div><div class="vela-live-banner-text"><strong>Connecting you to a human agent...</strong><span>${pos} - ${eta}</span></div>`
      card.insertBefore(banner, body.nextSibling)
    } else if (this.liveChatStatus === 'active' && this.agentName) {
      const banner = document.createElement('div')
      banner.className = 'vela-live-banner'
      const av = document.createElement('span')
      av.className = 'vela-live-banner-avatar'
      av.textContent = this.agentInitials ?? this.agentName.charAt(0)
      banner.appendChild(av)
      const text = document.createElement('div')
      text.className = 'vela-live-banner-text'
      text.innerHTML = `<strong>${escapeHtml(this.agentName)}</strong><span>from support is helping you</span>`
      banner.appendChild(text)
      card.insertBefore(banner, body.nextSibling)
    } else if (this.liveChatStatus === 'resolved') {
      const banner = document.createElement('div')
      banner.className = 'vela-live-banner'
      banner.textContent = 'This conversation has been resolved.'
      card.insertBefore(banner, body.nextSibling)
    } else if (this.escalated) {
      const e = document.createElement('div')
      e.className = 'vela-escalation'
      e.innerHTML = '<div class="vela-spinner"></div><div>Connecting you to a human agent...</div>'
      card.insertBefore(e, body.nextSibling)
    }

    // Composer — always rendered. Disabled once the chat is resolved so the
    // visitor can read the transcript but can't send follow-ups.
    const composer = this.renderComposer()
    card.appendChild(composer)

    // Auto-scroll the messages area to the bottom on every render.
    requestAnimationFrame(() => { body.scrollTop = body.scrollHeight })
  }

  private renderComposer(): HTMLElement {
    const outer = document.createElement('div')
    const resolved = this.liveChatStatus === 'resolved'
    // Small "Talk to a human" link — only shown while still on the bot.
    // Once the live chat is live (or resolved) the link disappears.
    if (this.liveChatStatus === 'none' && !resolved) {
      const link = document.createElement('button')
      link.className = 'vela-link-button'
      link.textContent = 'Prefer to talk to a person?'
      link.addEventListener('click', () => { void this.requestHuman() })
      outer.appendChild(link)
    }

    const wrap = document.createElement('div')
    wrap.className = 'vela-input'
    outer.appendChild(wrap)

    const ta = document.createElement('textarea')
    ta.placeholder = resolved ? 'Chat resolved — start a new conversation to continue.' : (this.cfg.inputPlaceholder || 'Type a message…')
    ta.rows = 1
    ta.disabled = resolved
    ta.setAttribute('aria-label', 'Type a message')
    autoResize(ta)
    wrap.appendChild(ta)

    const btn = document.createElement('button')
    btn.setAttribute('aria-label', 'Send')
    const sendStyle = this.cfg.sendButtonStyle || 'icon'
    if (sendStyle === 'text') btn.textContent = 'Send'
    else if (sendStyle === 'both') btn.innerHTML = `${ICONS.send}<span>Send</span>`
    else btn.innerHTML = ICONS.send
    btn.disabled = true
    wrap.appendChild(btn)

    ta.addEventListener('input', () => {
      btn.disabled = ta.value.trim().length === 0
      autoResize(ta)
      // Emit typing indicator while the visitor is composing to the agent.
      if (this.liveChatStatus === 'active' && this.socket && this.liveChatId) {
        this.socket.emit('livechat:visitor_typing', {
          chatId: this.liveChatId,
          isTyping: ta.value.trim().length > 0,
        })
      }
    })
    ta.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (!btn.disabled) this.send(ta.value.trim())
      }
    })
    // Also stop propagation at the input so keystrokes don't reach any host-
    // page listeners that attached at the capture phase (host-level stop runs
    // at bubble). Redundant with boot(), but cheap and defensive.
    ta.addEventListener('keydown', (e) => e.stopPropagation())
    ta.addEventListener('keyup', (e) => e.stopPropagation())
    btn.addEventListener('click', () => {
      if (!btn.disabled) this.send(ta.value.trim())
    })

    setTimeout(() => ta.focus(), 100)
    return outer
  }

  private async send(text: string): Promise<void> {
    if (!text || !this.sessionId) return
    this.chat.push({ who: 'user', bubbles: [text], ts: new Date() })
    this.render()

    // Once a human agent is connected, every visitor message goes through
    // the live-chat endpoint — the bot stays silent.
    if (this.liveChatStatus === 'active' && this.liveChatId) {
      await this.sendLiveChatMessage(text)
      return
    }

    this.typing = true
    this.filler = null
    this.render()

    try {
      const out = await this.sendMessage(text)
      if (out.fillerPhrase) {
        this.filler = out.fillerPhrase
        this.render()
      }
      const delay = Math.max(400, Math.min(out.typingDelayMs ?? 800, 4000))
      await wait(delay)
      this.typing = false
      this.filler = null
      if (out.escalate) this.escalated = true
      this.chat.push({ who: 'bot', bubbles: out.response, ts: new Date() })
      if (out.escalate && out.escalationMessage) {
        this.chat.push({ who: 'bot', bubbles: [out.escalationMessage], ts: new Date() })
      }
      this.render()
      if (out.escalate) {
        // Transition from bot → queue. requestHuman() sets queuePosition and
        // opens the socket; the LiveChat backend does the rest.
        await this.requestHuman()
      }
      // Bot ended the conversation (farewell) → offer a satisfaction rating.
      if (out.sessionEnded) this.maybeShowCsat()
    } catch (err) {
      this.typing = false
      this.filler = null
      this.chat.push({
        who: 'bot',
        bubbles: ["Sorry — something went wrong. Please try again or use the contact form."],
        ts: new Date(),
      })
      this.render()
      // eslint-disable-next-line no-console
      console.warn('[vela-widget] send failed', err)
    }
  }

  /**
   * Kick off a live-chat request and open the realtime socket. Safe to call
   * more than once — the backend returns the same row for a given session.
   */
  private async requestHuman(): Promise<void> {
    if (this.liveChatStatus !== 'none' || !this.sessionId) return
    try {
      const r = await fetch(`${apiBase}/api/v1/widget/live-chat/request`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-vela-key': apiKey },
        body: JSON.stringify({ sessionId: this.sessionId, visitorId: getVisitorId() }),
      })
      if (!r.ok) throw new Error(`live-chat request http ${r.status}`)
      const body = (await r.json()) as { data: { chatId: string; status: string; queuePosition: number | null } }
      this.liveChatId = body.data.chatId
      this.liveChatStatus = body.data.status === 'active' ? 'active' : 'waiting'
      this.queuePosition = body.data.queuePosition
      this.openSocket()
      this.render()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[vela-widget] live-chat request failed', err)
    }
  }

  private async sendLiveChatMessage(text: string): Promise<void> {
    try {
      const r = await fetch(`${apiBase}/api/v1/widget/live-chat/message`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-vela-key': apiKey },
        body: JSON.stringify({
          chatId: this.liveChatId,
          visitorId: getVisitorId(),
          content: text,
        }),
      })
      if (!r.ok) throw new Error(`live-chat message http ${r.status}`)
    } catch (err) {
      this.chat.push({
        who: 'bot',
        bubbles: ["Sorry — couldn't deliver that. Please try again."],
        ts: new Date(),
      })
      this.render()
      // eslint-disable-next-line no-console
      console.warn('[vela-widget] live-chat send failed', err)
    }
  }

  /** Connect the socket to the visitor's session room and wire handlers. */
  private openSocket(): void {
    if (this.socket || !this.sessionId) return
    const socket = io(apiBase || window.location.origin, {
      path: '/socket.io',
      auth: {
        widgetKey: apiKey,
        sessionId: this.sessionId,
        visitorId: getVisitorId(),
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
    })
    this.socket = socket

    socket.on('livechat:agent_joined', (e: { agentName: string; agentInitials: string }) => {
      this.liveChatStatus = 'active'
      this.queuePosition = null
      this.agentName = e.agentName
      this.agentInitials = e.agentInitials
      this.chat.push({
        who: 'bot',
        bubbles: [`${e.agentName} has joined the chat.`],
        ts: new Date(),
      })
      this.render()
    })
    socket.on('livechat:queue_update', (e: { chatId: string; queuePosition: number }) => {
      if (e.chatId === this.liveChatId) {
        this.queuePosition = e.queuePosition
        this.render()
      }
    })
    socket.on('livechat:message', (e: {
      chatId: string; role: 'agent' | 'visitor' | 'system';
      content: string; agentName: string | null;
    }) => {
      if (e.chatId !== this.liveChatId) return
      // Echo of our own visitor message is ignored — we already painted it.
      if (e.role === 'visitor') return
      this.agentTyping = false
      this.chat.push({
        who: 'bot',
        bubbles: [e.content],
        ts: new Date(),
      })
      this.render()
    })
    socket.on('livechat:typing', (e: { chatId: string; from: 'agent' | 'visitor'; isTyping: boolean }) => {
      if (e.chatId !== this.liveChatId || e.from !== 'agent') return
      this.agentTyping = e.isTyping
      this.render()
    })
    socket.on('livechat:resolved', (e: { chatId: string; message: string }) => {
      if (e.chatId !== this.liveChatId) return
      this.liveChatStatus = 'resolved'
      this.chat.push({ who: 'bot', bubbles: [e.message], ts: new Date() })
      this.render()
      socket.disconnect()
      this.socket = null
      // Agent resolved the chat → offer a satisfaction rating.
      this.maybeShowCsat()
    })
  }

  /* ---------- CSAT (satisfaction rating) ---------- */

  // Show the rating overlay once per session, 500ms after the session ends,
  // when the persona has CSAT enabled. Purely additive — never blocks chat.
  private maybeShowCsat(): void {
    if (this.csatShown || !this.cfg.enableCsat || !this.sessionId) return
    this.csatShown = true
    window.setTimeout(() => this.renderCsat(), 500)
  }

  private renderCsat(): void {
    const card = this.root.querySelector('.vela-card')
    if (!card) return
    let rating = 0

    const overlay = document.createElement('div')
    overlay.className = 'vela-csat-overlay'
    overlay.setAttribute('role', 'dialog')
    overlay.innerHTML = `
      <div class="vela-csat-card">
        <p class="vela-csat-title">How was your experience?</p>
        <div class="vela-csat-stars">
          ${[1, 2, 3, 4, 5].map((n) => `<button class="vela-star" data-rating="${n}" aria-label="${n} star">★</button>`).join('')}
        </div>
        <textarea class="vela-csat-comment" placeholder="Any feedback? (optional)" rows="2"></textarea>
        <div class="vela-csat-actions">
          <button class="vela-csat-submit">Submit</button>
          <button class="vela-csat-skip">Skip</button>
        </div>
      </div>`

    const stars = Array.from(overlay.querySelectorAll('.vela-star')) as HTMLButtonElement[]
    const paint = () => stars.forEach((s, i) => { s.style.color = i < rating ? 'var(--vela-color-widget)' : '#D1D5DB' })
    paint()
    stars.forEach((s) => s.addEventListener('click', () => { rating = Number(s.dataset.rating); paint() }))

    const submit = overlay.querySelector('.vela-csat-submit') as HTMLButtonElement
    const skip = overlay.querySelector('.vela-csat-skip') as HTMLButtonElement
    const comment = overlay.querySelector('.vela-csat-comment') as HTMLTextAreaElement
    const submitBtn = submit
    submitBtn.style.background = 'var(--vela-color-widget)'

    skip.addEventListener('click', () => overlay.remove())
    submit.addEventListener('click', () => {
      if (rating < 1) { paint(); return }
      submit.disabled = true
      submit.textContent = 'Sending…'
      void this.submitCsat(rating, comment.value.trim() || undefined).then(() => {
        overlay.innerHTML = '<div class="vela-csat-card"><p class="vela-csat-title">Thank you!</p></div>'
        window.setTimeout(() => overlay.remove(), 1500)
      }).catch(() => { overlay.remove() })
    })

    card.appendChild(overlay)
  }

  private async submitCsat(score: number, comment?: string): Promise<void> {
    await fetch(`${apiBase}/api/v1/widget/csat`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-vela-key': apiKey },
      body: JSON.stringify({ sessionId: this.sessionId, score, comment }),
    })
  }

  private async sendMessage(text: string): Promise<MessageResp> {
    const r = await fetch(`${apiBase}/api/v1/widget/message`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-vela-key': apiKey },
      body: JSON.stringify({
        sessionId: this.sessionId,
        message: text,
        visitorId: getVisitorId(),
      }),
    })
    if (!r.ok) throw new Error(`message http ${r.status}`)
    const body = await r.json()
    return body.data as MessageResp
  }

  /* ---------- Contact form ---------- */

  private renderForm(body: HTMLElement): void {
    const form = document.createElement('form')
    form.className = 'vela-form'
    form.noValidate = true

    const fields: Array<[string, string, string, boolean, 'input' | 'textarea']> = [
      ['name',    'Name',    'text',  true,  'input'],
      ['email',   'Email',   'email', true,  'input'],
      ['phone',   'Phone',   'tel',   false, 'input'],
      ['company', 'Company', 'text',  false, 'input'],
      ['subject', 'Subject', 'text',  false, 'input'],
      ['message', 'Message', 'text',  true,  'textarea'],
    ]

    const inputs: Record<string, HTMLInputElement | HTMLTextAreaElement> = {}
    for (const [key, label, type, required, kind] of fields) {
      const wrap = document.createElement('div')
      wrap.className = 'vela-field'
      const lab = document.createElement('label')
      lab.htmlFor = `vela-${key}`
      lab.textContent = label + (required ? ' *' : '')
      wrap.appendChild(lab)
      const inp = kind === 'textarea'
        ? document.createElement('textarea')
        : document.createElement('input')
      inp.id = `vela-${key}`
      inp.name = key
      if (inp instanceof HTMLInputElement) inp.type = type
      if (required) inp.required = true
      wrap.appendChild(inp)
      inputs[key] = inp
      form.appendChild(wrap)
    }

    const err = document.createElement('div')
    err.className = 'vela-form-error'
    err.style.display = 'none'
    form.appendChild(err)

    const submit = document.createElement('button')
    submit.type = 'submit'
    submit.textContent = 'Send message'
    form.appendChild(submit)

    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      err.style.display = 'none'
      const payload = {
        name:    (inputs.name!    as HTMLInputElement).value.trim(),
        email:   (inputs.email!   as HTMLInputElement).value.trim(),
        phone:   (inputs.phone!   as HTMLInputElement).value.trim() || undefined,
        company: (inputs.company! as HTMLInputElement).value.trim() || undefined,
        subject: (inputs.subject! as HTMLInputElement).value.trim() || undefined,
        message: (inputs.message! as HTMLTextAreaElement).value.trim(),
      }
      if (!payload.name || !payload.email || !payload.message) {
        err.textContent = 'Name, email and message are required.'
        err.style.display = 'block'
        return
      }
      submit.disabled = true
      submit.textContent = 'Sending…'
      try {
        const r = await fetch(`${apiBase}/api/v1/widget/${encodeURIComponent(apiKey)}/contact-form`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'x-vela-key': apiKey },
          body: JSON.stringify(payload),
        })
        if (!r.ok) throw new Error(`form http ${r.status}`)
        const json = await r.json() as {
          data?: { route?: 'lead' | 'case'; caseNumber?: number | null; message?: string }
        }
        const self = this as Widget & {
          thanksEmail?: string
          thanksName?: string
          thanksRoute?: 'lead' | 'case'
          thanksCaseNumber?: number | null
        }
        self.thanksEmail = payload.email
        self.thanksName = payload.name
        self.thanksRoute = json.data?.route ?? 'case'
        self.thanksCaseNumber = json.data?.caseNumber ?? null
        this.open('thanks')
      } catch (e) {
        err.textContent = "Sorry — couldn't send your message. Please try again."
        err.style.display = 'block'
        submit.disabled = false
        submit.textContent = 'Send message'
        // eslint-disable-next-line no-console
        console.warn('[vela-widget] contact form failed', e)
      }
    })

    body.appendChild(form)
  }

  /* ---------- Thanks ---------- */

  private renderThanks(body: HTMLElement): void {
    const self = this as Widget & {
      thanksEmail?: string
      thanksName?: string
      thanksRoute?: 'lead' | 'case'
      thanksCaseNumber?: number | null
    }
    const email = self.thanksEmail ?? ''
    const firstName = (self.thanksName ?? '').split(/\s+/)[0] ?? ''
    const route = self.thanksRoute ?? 'lead'
    const caseNumber = self.thanksCaseNumber ?? null

    const wrap = document.createElement('div')
    wrap.className = 'vela-thanks'

    const checkCircle = document.createElement('div')
    checkCircle.className = 'vela-check'
    checkCircle.textContent = '✓'
    wrap.appendChild(checkCircle)

    const title = document.createElement('h3')
    title.textContent = 'Message sent!'
    wrap.appendChild(title)

    // Body — always renders visible copy. Earlier versions could fall
    // through to an empty string when route/firstName/email were all
    // missing from the API response; the chained fallback below
    // guarantees we never ship a blank success card.
    const sub = document.createElement('p')
    let bodyText: string
    if (route === 'case' && caseNumber != null) {
      bodyText = email
        ? `Support ticket #${caseNumber} created. We'll reply to ${email} within 4 hours.`
        : `Support ticket #${caseNumber} created. We'll be in touch within 4 hours.`
    } else if (firstName && email) {
      bodyText = `Thanks ${firstName}! We'll reply to ${email} within 24 hours.`
    } else if (firstName) {
      bodyText = `Thanks ${firstName}! We received your message and will get back to you shortly.`
    } else if (email) {
      bodyText = `Thanks! We received your message and will reply to ${email} shortly.`
    } else {
      bodyText = 'Thanks! We received your message and will get back to you shortly.'
    }
    sub.textContent = bodyText
    wrap.appendChild(sub)

    const restart = document.createElement('button')
    restart.type = 'button'
    restart.textContent = 'Send another message'
    restart.addEventListener('click', (e) => {
      e.preventDefault()
      this.open('picker')
    })
    wrap.appendChild(restart)

    body.appendChild(wrap)
  }
}

/* ---------------------------------------------------------------------- */
/* Helpers                                                                 */
/* ---------------------------------------------------------------------- */

function darken(hex: string, percent: number): string {
  const n = parseInt(hex.replace('#', ''), 16)
  if (Number.isNaN(n)) return hex
  let r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff
  r = Math.max(0, Math.floor(r * (1 - percent / 100)))
  g = Math.max(0, Math.floor(g * (1 - percent / 100)))
  b = Math.max(0, Math.floor(b * (1 - percent / 100)))
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')
}

function hexToRgb(hex: string): string {
  const cleaned = hex.replace('#', '').trim()
  const normalized = cleaned.length === 3
    ? cleaned.split('').map((char) => char + char).join('')
    : cleaned
  const n = parseInt(normalized, 16)
  if (Number.isNaN(n)) return '245, 158, 11'
  const r = (n >> 16) & 0xff
  const g = (n >> 8) & 0xff
  const b = n & 0xff
  return `${r}, ${g}, ${b}`
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

function autoResize(el: HTMLTextAreaElement): void {
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, 120)}px`
}

/* ---------------------------------------------------------------------- */
/* Inline SVG icon set (no external font required)                         */
/* ---------------------------------------------------------------------- */

const ICONS = {
  aiChat: '<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M2 4C2 2.9 2.9 2 4 2H14C15.1 2 16 2.9 16 4V10C16 11.1 15.1 12 14 12H8L4 16V12H4C2.9 12 2 11.1 2 10V4Z"/><path d="M10 6H17C18.1 6 19 6.9 19 8V13C19 14.1 18.1 15 17 15H16V18L12.8 15H10C8.9 15 8 14.1 8 13V12H14C15.7 12 17 10.7 17 9V6H10Z" opacity="0.55"/></svg>',
  welcomeMark: '<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="18" cy="18" r="12" fill="currentColor"/><circle cx="30" cy="18" r="12" fill="currentColor" fill-opacity="0.6"/><circle cx="24" cy="30" r="12" fill="currentColor" fill-opacity="0.3"/></svg>',
  bot:   '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="11" rx="4"/><path d="M12 4v4"/><circle cx="9" cy="13" r="1"/><circle cx="15" cy="13" r="1"/><path d="M8 17h8"/></svg>',
  chat:  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
  message: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  help:  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  doc:   '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>',
  mailOpen: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v4.5"/><path d="M2 6l10 7 10-7"/><path d="M4 20h16a2 2 0 0 0 2-2v-7.5"/><path d="M2 10.5V18a2 2 0 0 0 2 2"/></svg>',
  phone: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.11 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.63 2.61a2 2 0 0 1-.45 2.11L8 9.91a16 16 0 0 0 6.09 6.09l1.47-1.28a2 2 0 0 1 2.11-.45c.84.3 1.71.51 2.61.63A2 2 0 0 1 22 16.92z"/></svg>',
  whatsapp: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.6 6.3A8 8 0 0 0 4 12c0 1.4.4 2.8 1 4l-1.1 4.1 4.2-1.1c1.2.6 2.5 1 3.9 1a8 8 0 0 0 5.6-13.7zm-5.6 12.3a6.7 6.7 0 0 1-3.4-.9l-.2-.1-2.5.7.7-2.4-.2-.3a6.6 6.6 0 1 1 12.3-3.3 6.6 6.6 0 0 1-6.7 6.3zm3.6-5c-.2-.1-1.2-.6-1.4-.6-.2-.1-.3-.1-.5.1-.1.2-.5.6-.6.7-.1.1-.2.2-.4 0-.2-.1-.8-.3-1.5-1-.6-.5-.9-1.1-1.1-1.3-.1-.2 0-.3.1-.4l.3-.4c.1-.1.1-.2.2-.4 0-.2 0-.3-.1-.4 0-.1-.5-1.2-.7-1.6-.2-.4-.4-.4-.5-.4h-.4c-.1 0-.4.1-.6.3-.2.2-.8.7-.8 1.8 0 1 .8 2 .9 2.1.1.2 1.6 2.4 3.8 3.4 1.4.6 2 .6 2.7.5.4-.1 1.2-.5 1.4-.9.2-.5.2-.9.1-1l-.4-.2z"/></svg>',
  back:  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>',
  close: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
  send:  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>',
  check: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
}

/* ---------------------------------------------------------------------- */
/* Cookie consent banner (GDPR Art. 7 + ePrivacy)                          */
/* ---------------------------------------------------------------------- */

const COOKIE_STORAGE_KEY = 'vela_cookie_consent'

function hasStoredCookieConsent(currentVersion: string | null): boolean {
  try {
    const raw = localStorage.getItem(COOKIE_STORAGE_KEY)
    if (!raw) return false
    const stored = JSON.parse(raw) as CookiePrefs
    // When the tenant publishes a new policy version, invalidate prior consent
    // so the banner shows again — this is the only legally-clean way to get
    // re-consent for updated processing.
    if (currentVersion && stored.version !== currentVersion) return false
    return true
  } catch {
    return false
  }
}

function saveCookiePrefs(prefs: CookiePrefs): void {
  try { localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify(prefs)) }
  catch { /* private mode — consent still valid for the session */ }
}

function mountCookieBanner(root: ShadowRoot, cfg: WidgetConfig, widget: Widget): void {
  const banner = document.createElement('div')
  banner.className = 'vela-cookie'
  banner.setAttribute('role', 'dialog')
  banner.setAttribute('aria-live', 'polite')
  banner.setAttribute('aria-label', 'Cookie preferences')

  banner.innerHTML = `
    <h3>We use cookies</h3>
    <p>We use cookies to improve your experience. Choose your preferences:</p>
    <label><input type="checkbox" checked disabled /> <span>Necessary (required)</span></label>
    <label><input type="checkbox" data-cat="functional" /> <span>Functional</span></label>
    <label><input type="checkbox" data-cat="analytics" /> <span>Analytics</span></label>
    <label><input type="checkbox" data-cat="marketing" /> <span>Marketing</span></label>
    <div class="vela-cookie-actions">
      <button type="button" class="vela-cookie-save">Save preferences</button>
      <button type="button" class="vela-cookie-accept">Accept all</button>
    </div>
    ${cfg.cookieConsent?.policyUrl ? `<a class="vela-cookie-link" href="${escapeHtml(cfg.cookieConsent.policyUrl)}" target="_blank" rel="noopener">Privacy policy</a>` : ''}
  `
  root.appendChild(banner)

  const inputs = {
    analytics: banner.querySelector('input[data-cat="analytics"]') as HTMLInputElement,
    functional: banner.querySelector('input[data-cat="functional"]') as HTMLInputElement,
    marketing: banner.querySelector('input[data-cat="marketing"]') as HTMLInputElement,
  }

  async function submit(prefs: { analytics: boolean; functional: boolean; marketing: boolean }) {
    const stored: CookiePrefs = {
      necessary: true,
      analytics: prefs.analytics,
      functional: prefs.functional,
      marketing: prefs.marketing,
      version: cfg.cookieConsent?.policyVersion ?? null,
      savedAt: new Date().toISOString(),
    }
    saveCookiePrefs(stored)
    try {
      await fetch(`${apiBase}/api/v1/widget/cookie-consent`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-vela-key': apiKey },
        body: JSON.stringify({
          visitorId: getVisitorId(),
          analytics: prefs.analytics,
          functional: prefs.functional,
          marketing: prefs.marketing,
          consentVersion: cfg.cookieConsent?.policyVersion ?? undefined,
        }),
      })
    } catch {
      // Best-effort — local storage is authoritative for the session.
    }
    banner.remove()
    // Tell the widget something changed — currently nothing to refresh, but
    // the hook is here so future features (analytics consent gating etc.)
    // can react without another round trip.
    void widget
  }

  banner.querySelector('.vela-cookie-accept')!.addEventListener('click', () => {
    void submit({ analytics: true, functional: true, marketing: true })
  })
  banner.querySelector('.vela-cookie-save')!.addEventListener('click', () => {
    void submit({
      analytics: inputs.analytics.checked,
      functional: inputs.functional.checked,
      marketing: inputs.marketing.checked,
    })
  })
}
